import { SysOrgModule, SysOrgRoleName } from "~/api";
import ContactActivityList from "~/Compliance/pages/ContactActivityList.vue";
import ContactListingPage from "~/Compliance/pages/ContactListingPage.vue";
import CRMEntityDetail from "~/Compliance/pages/CRMEntityDetail.vue";
import TreeDefinitionList from "~/Compliance/pages/TreeDefinitionList.vue";
import RouterViewWrapper from "~/components/RouterViewWrapper.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { registerEnumDescriptions } from "~/utils/enumDescriptions";
import { refreshCaches } from "~/utils/miscUtils";
import HMBLayout from "~/YYC_Housing/HMB/layouts/HMBLayout.vue";
import Dashboard from "~/YYC_Housing/HMB/pages/Dashboard.vue";
import DataExtracts from "~/YYC_Housing/HMB/pages/DataExtracts.vue";
import ApplicationPageLayout from "~/YYC_Housing/Intake/layouts/ApplicationPageLayout.vue";
import IntakeLayout from "~/YYC_Housing/Intake/layouts/IntakeLayout.vue";
import ApplicantHome from "~/YYC_Housing/Intake/pages/ApplicantHome.vue";
import ApplicationWizard from "~/YYC_Housing/Intake/pages/ApplicationWizard.vue";
import {
  housingApplicationStatusDescriptions,
  housingIntakeScenarioApplicationStatusDescriptions,
  housingIntakeSenarioStatusDescriptions,
} from "../utils/enumDescriptions";

// Register housing enum descriptions at import time
registerEnumDescriptions({
  housingIntakeScenarioStatus: housingIntakeSenarioStatusDescriptions,
  housingIntakeScenarioApplicationStatus:
    housingIntakeScenarioApplicationStatusDescriptions,
  housingApplicationStatus: housingApplicationStatusDescriptions,
});

let refreshedForHavn = false;

type metaInfo = {
  anonymous?: boolean;
  HMB?: boolean;
  SeniorsSide?: boolean;
};

const anonymousMeta: metaInfo = { anonymous: true };
const hmbMeta: metaInfo = { HMB: true };

/**
 * Housing-specific routes: Intake (public portal) and HMB (staff portal).
 * Spread these into the main applicationRouteMap.
 */
export const housingRoutes = {
  Intake: {
    path: "/intake",
    component: IntakeLayout,
    name: "Public Site",
    redirect: () => {
      return { path: "/finder" };
    },
    children: {
      Finder: {
        path: "/finder",
        meta: anonymousMeta,
        component: () => import("~/YYC_Housing/Intake/pages/FinderMain.vue"),
        name: "Explore Locations",
      },

      NotFound: {
        path: "/404",
        meta: anonymousMeta,
        component: () => import("~/pages/Client404.vue"),
        name: "NotFound",
      },

      Application: {
        path: "/application",
        component: ApplicationPageLayout,
        name: "Application",
        redirect: () => {
          return { name: "applicationHome" };
        },
        children: {
          ApplicationHome: {
            path: "/application/home",
            component: ApplicantHome,
            name: "applicationHome",
          },

          ApplicationWizard: {
            path: "/application/:applicationGuid/wizard/:step",
            component: ApplicationWizard,
            props: (route: any) => ({
              step: Number.parseInt(route.params.step),
              applicationGuid: route.params.applicationGuid,
            }),
            name: "applicationWizard",
          },
          // FormResponse: {
          //   path: "/application/forms/:formGuid/responses/:responseGuid",
          //   component: IntakeFormResponse,
          //   name: "formResponse",
          //   props: true,
          // },
          WorkflowResponse: {
            path: "/application/workflows/:workflowGuid",
            component: () =>
              import("~/YYC_Housing/Intake/pages/IntakeWorkflowResponse.vue"),
            name: "intakeWorkflowResponse",
            props: true,
          },
        },
      },
    },
  },

  HMB: {
    path: "/portal",
    component: HMBLayout,
    meta: hmbMeta,
    name: "Portal",
    redirect: () => {
      return { path: "/portal/dashboard" };
    },
    children: {
      Dashboard: {
        component: Dashboard,
        name: "Dashboard",
        path: "dashboard",
        meta: {
          display: "Summaries",
          roles: [SysOrgRoleName.MEMBER],
        },
      },
      Process: {
        path: "process",
        redirect: () => {
          return { path: "/portal/process/new-review" };
        },
        children: {
          OfflineApplications: {
            path: "offline-applications",
            component: () =>
              import("~/YYC_Housing/HMB/pages/OfflineApplications.vue"),
            name: "Draft",
          },
          NewReview: {
            path: "new-review",
            component: () => import("~/YYC_Housing/HMB/pages/NewPRS.vue"),
            name: "New & Review",
          },
          OnHold: {
            path: "on-hold",
            component: () => import("~/YYC_Housing/HMB/pages/OnHold.vue"),
            name: "On Hold",
          },
          Waitlist: {
            path: "waitlist",
            component: () => import("~/YYC_Housing/HMB/pages/Waitlist.vue"),
            name: "Waitlist",
          },
          IntakeOffer: {
            path: "intake-offer",
            component: () =>
              import("~/YYC_Housing/HMB/pages/VacancyManagement2.vue"),
            name: "Vacancy Management",
          },
        },
      },
      ContactManagement: {
        path: "",
        name: "PortalCRM",
        component: RouterViewWrapper,
        redirect: () => {
          return { path: "/portal/dashboard" };
        },
        children: {
          ContactActivities: {
            path: "workflows/contact-activities",
            meta: {
              display: "Contact Activities",
              roles: [SysOrgRoleName.MEMBER],
              entityName: "Contact Activity",
            },
            component: ContactActivityList,
          },
          Contacts: {
            path: "contact-management/contacts",
            meta: {
              display: "Contacts",
              roles: [SysOrgRoleName.MEMBER],
              entityName: "Contact",
            },
            component: ContactListingPage,
          },
          HousingOrganizations: {
            path: "contact-management/housing-organizations",
            meta: {
              display: "Organizations",
              roles: [SysOrgRoleName.MEMBER],
              entityName: "Housing Organization",
            },
            component: () =>
              import("~/YYC_Housing/HMB/pages/CRMHousingOrgList.vue"),
          },
          HousingApplications: {
            path: "contact-management/housing-applications",
            meta: {
              display: "Housing Applications",
              roles: [SysOrgRoleName.MEMBER],
              entityName: "Housing Application",
            },
            component: () =>
              import("~/YYC_Housing/HMB/pages/ListApplications.vue"),
          },
        },
      },

      CRMDetail: {
        path: "contact-management/:entityDefinitionGuid/:entityGuid",
        name: "PortalCRMEntityDetail",
        meta: {
          display: "CRM Detail",
          roles: [SysOrgRoleName.MEMBER],
        },
        component: CRMEntityDetail,
      },

      CRMHousingOrgDetail: {
        path: "contact-management/housing-org/:housingOrgGuid",
        name: "PortalCRMHousingOrgDetail",
        meta: {
          display: "Housing Org Detail",
          roles: [SysOrgRoleName.MEMBER],
        },
        component: () =>
          import("~/YYC_Housing/HMB/pages/CRMHousingOrgDetail.vue"),
      },

      Admin: {
        path: "admin",
        redirect: () => {
          return { path: "/portal/sysadmin/sites" };
        },
        children: {
          HMBs: {
            path: "hmbs",
            component: () => import("~/YYC_Housing/HMB/pages/HMBList.vue"),
            name: "Organizations Setup",
          },
          PropertiesList: {
            path: "properties",
            component: () => import("~/YYC_Housing/HMB/pages/SitesList.vue"),
            name: "Property Setup",
          },
          DefineFeatures: {
            path: "defineFeatures",
            component: () =>
              import("~/YYC_Housing/HMB/pages/SiteDetailsList.vue"),
            name: "Define Features",
          },
          DataExtracts: {
            path: "dataExtracts",
            component: DataExtracts,
            name: "Data Reporting",
          },
          PublicUsers: {
            path: "publicUsers",
            component: () => import("~/YYC_Housing/HMB/pages/PublicUsers.vue"),
            name: "Public Users",
          },
          PortalUsers: {
            path: "portalUsers",
            component: () => import("~/YYC_Housing/HMB/pages/PortalUsers.vue"),
            name: "Portal Users",
          },
        },
      },

      SysAdmin: {
        path: "sysAdmin",
        redirect: () => {
          return { path: "/" };
        },
        children: {
          DesignForms: {
            path: "forms",
            component: () => import("~/YYC_Housing/HMB/pages/DesignForms.vue"),
            name: "Design Forms",
          },
          BulkUpload: {
            path: "import",
            component: () => import("~/YYC_Housing/HMB/pages/BulkImport.vue"),
            name: "Bulk Import",
          },
          Logs: {
            path: "logs",
            component: () => import("~/YYC_Housing/HMB/pages/LogViewer.vue"),
            name: "Logs",
          },
          TreeDefinitions: {
            path: "treeDefinitions",
            component: TreeDefinitionList,
            name: "Tree Definitions",
          },
          SectorUnitTypeMappings: {
            path: "sectorUnitTypeMappings",
            component: () =>
              import("~/YYC_Housing/HMB/pages/SectorUnitTypeMappings.vue"),
            name: "Sector → Unit Type Mapping",
          },
          FormDesigner: {
            path: "/forms/:guid/design",
            component: () =>
              import("~/NewFormBuilder/pages/FormDesignerPage.vue"),
            name: "Form Designer",
            props: true,
          },
          FormResponses: {
            path: "forms/:guid/responses",
            component: () =>
              import("~/NewFormBuilder/pages/FormResponsesList.vue"),
            name: "Form Responses",
            props: true,
          },
          FormRespond: {
            path: "forms/:formGuid/responses/:responseGuid",
            component: () => import("~/NewFormBuilder/pages/FormResponse.vue"),
            name: "Form Response",
            props: true,
          },
        },
      },

      Migrations: {
        path: "migrations",
        redirect: () => {
          return { name: "Cross DB Sync" };
        },
        children: {
          CrossDbSync: {
            path: "cross-db-sync",
            component: () => import("~/Compliance/pages/CrossDbSync.vue"),
            name: "Cross DB Sync",
          },
          OldHousingDataImport: {
            path: "old-housing-data-import",
            component: () =>
              import("~/Compliance/pages/OldHousingDataImport.vue"),
            name: "1. Old Housing Data Import",
          },
          HousingApplicationMigration: {
            path: "housing-application-migration",
            component: () =>
              import("~/YYC_Housing/HMB/pages/HousingApplicationMigration.vue"),
            name: "2. Housing Application Migration",
          },
          HousingFormResponsesAudit: {
            path: "housing-form-responses",
            component: () =>
              import("~/Compliance/pages/HousingFormResponsesAudit.vue"),
            name: "3. Housing Responses",
          },
          EntityBackfill: {
            path: "entity-backfill",
            component: () =>
              import("~/YYC_Housing/HMB/pages/EntityBackfill.vue"),
            name: "4. Entity Backfill",
          },
          PeopleMigration: {
            path: "people-migration",
            component: () =>
              import("~/Compliance/pages/PersonMigrationMapping.vue"),
            name: "5. People Migration",
          },
          SiteUnitConfigBackfill: {
            path: "site-unit-config-backfill",
            component: () =>
              import("~/YYC_Housing/HMB/pages/SiteUnitConfigBackfill.vue"),
            name: "6. Site Unit Config Backfill",
          },
          SiteResultConfigBackfill: {
            path: "site-result-config-backfill",
            component: () =>
              import("~/YYC_Housing/HMB/pages/SiteResultConfigBackfill.vue"),
            name: "7. Finder Data Migrations",
          },
          ScenarioMigration: {
            path: "scenario-migration",
            component: () =>
              import("~/YYC_Housing/HMB/pages/ScenarioMigration.vue"),
            name: "8. Scenario Migration",
          },
        },
      },
    },
  },
};

/**
 * Housing-specific route guard for HMB/SeniorsSide routes.
 * Call this from the main router's beforeEach handler.
 * Returns a route to redirect to, true to continue, or undefined to skip.
 */
export async function housingRouteGuard(
  to: any,
  applicationRouteMap: any,
): Promise<any> {
  // console.log("ROUTER: Checking housing route guard for route:", to.name);

  const allMetas = to.matched.map((m: any) => m.meta as metaInfo);
  const hasHMBMeta = allMetas.some((m: metaInfo) => m.HMB);
  const hasSeniorsSideMeta =
    allMetas.find((m: metaInfo) => m.SeniorsSide)?.SeniorsSide === true;

  // Only handle HMB or SeniorsSide routes
  if (!hasHMBMeta && !hasSeniorsSideMeta) {
    return undefined; // Not a housing route, skip
  }

  const pageNotFound = { name: "NotFound" };

  // console.log("ROUTER: Loading YYC MyInfoStore for HMB/SeniorsSide route");

  const myInfoStore = useMyInfoStore();
  await myInfoStore.load();

  // If user is trying to access SeniorsSide (public site) but they're a portal user, redirect to dashboard
  if (hasSeniorsSideMeta && myInfoStore.isPortal) {
    return { name: "Dashboard" };
  }

  // For HMB (portal) routes, check if user has portal access
  if (hasHMBMeta) {
    // console.log("ROUTER: Checking portal access for HMB route");

    if (!myInfoStore.userInfo) {
      // Server doesn't think we are logged in
      return pageNotFound;
    }

    // Portal only works with the HAVN org — ensure it's selected
    // const userOrgGuids = myInfoStore.organizations.map((o: any) => o.guid);
    // if (!userOrgGuids.includes(havnOrgGuid)) {
    //   console.log("ROUTER: User does not belong to HAVN org, redirecting home");
    //   return { path: "/" };
    // }
    if (myInfoStore.currentSysOrg.modules.includes(SysOrgModule.HOUSING)) {
      // console.log("ROUTER: Switching to HAVN org for portal");
      if (!refreshedForHavn) {
        refreshedForHavn = true;
        refreshCaches();
      }
      return true; // Allow navigation to proceed; refreshCaches will reload the page at /portal
    }

    if (myInfoStore.isPortal) {
      // console.log("ROUTER: Access granted to HMB route");

      // User has portal access - check specific role requirements for certain pages
      if (
        (to.name ===
          applicationRouteMap.HMB.children.SysAdmin.children.DesignForms.name ||
          to.name ===
            applicationRouteMap.HMB.children.SysAdmin.children.FormDesigner
              .name ||
          to.name ===
            applicationRouteMap.HMB.children.SysAdmin.children.FormResponses
              .name ||
          to.name ===
            applicationRouteMap.HMB.children.SysAdmin.children.FormRespond
              .name ||
          to.name ===
            // use SetupBasic role here as a hack since only sunwapta will have it
            applicationRouteMap.HMB.children.SysAdmin.children.BulkUpload
              .name ||
          to.name ===
            applicationRouteMap.HMB.children.Migrations.children
              .SiteResultConfigBackfill.name ||
          to.name ===
            applicationRouteMap.HMB.children.Migrations.children
              .ScenarioMigration.name ||
          to.name ===
            applicationRouteMap.HMB.children.Migrations.children.EntityBackfill
              .name ||
          to.name ===
            applicationRouteMap.HMB.children.SysAdmin.children.TreeDefinitions
              .name ||
          to.name ===
            applicationRouteMap.HMB.children.SysAdmin.children
              .SectorUnitTypeMappings.name) &&
        !myInfoStore.currentSysOrg?.roles?.includes(SysOrgRoleName.SETUP_BASIC)
      ) {
        // console.log("ROUTER: Access denied - user lacks SetupBasic role");

        return pageNotFound;
      }
      return true;
    }

    // console.log("ROUTER: Access denied - user lacks portal access");

    // User doesn't have portal access
    return pageNotFound;
  }

  return undefined; // Not handled
}
